/** @jsx jsx */

import { Component, ComponentProps, Fragment } from 'react';
import { find, isEmpty, isEqual, omit, pick } from 'lodash';

import { jsx, css } from '@emotion/core';
import { CopyTextInput, Margin, Text } from '@zapier/design-system';

import DeveloperFormFields from 'app/developer-v3/components/DeveloperFormFields';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import EditorLayout from 'app/developer-v3/layouts/EditorLayout';
import HelpBox from 'app/developer-v3/components/HelpBox';
import NonFormaticFieldWrapper from 'app/developer-v3/components/NonFormaticFieldWrapper';
import OptionBlock from 'app/developer-v3/components/SetupStep/OptionBlock';
import RequestTester from 'app/developer-v3/components/RequestTester';
import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import SetupStep from 'app/developer-v3/components/SetupStep';
import TextOrCodeInput from 'app/developer-v3/components/TextOrCodeInput';
import { constants } from 'app/entities/EnvironmentVariable';
import {
  AUTH_SCHEMES,
  NEW_SERVICE_FIELD,
  highlightAuthFieldKey,
} from 'app/developer-v3/constants';
import {
  authMeetsRequirements,
  getAppVersionUrl,
  getAuthenticationQueryHighlight,
  getDeepLinkMetaInfoFromQuery,
  getHighlightAuthenticationFieldKey,
  removeNonNumericAndCastToNumber,
} from 'app/developer-v3/utils';

import AuthenticationFields from './AuthenticationFields';
import { getAuthFieldsForAuthType } from './utils';
import {
  AUTH_TYPE_HELP_TEXT,
  DEFAULT_REQUEST_CONFIGS,
  INPUT_FIELDS_ADDITIONAL_HELP_TEXT,
  STEPS_HELP_BY_TYPE,
  SETUP_STEPS,
} from './constants';
import {
  commonFieldKeys,
  commonFields,
  configFields,
  configFieldsLegacy,
  credentialFields,
  tokenExchangeFields,
} from './fieldTemplates';

import type { ApiHighlightField, QueryHighlight } from 'app/developer-v3/types';
import { AuthenticationSchema } from 'app/developer-v3/platformSchema/authentication';
import type { EnvironmentVariablesSchema } from 'app/developer-v3/platformSchema/environment';
import type { LegacyAuthenticationSchema } from 'app/developer-v3/platformSchema/legacy';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';
import type { VersionParams } from 'app/developer-v3/types/version';

import TestTriggerDropdown from './TestTriggerDropdown';

import type { FormValues } from './types';

const Styles = {
  root: css`
    .auth-tester.auth-buttons__button {
      display: none;
    }
  `,
};

type Props = ComponentProps<typeof AuthenticationFields> & {
  authentication: AuthenticationSchema;
  authenticationLegacy?: LegacyAuthenticationSchema;
  deleteAuthenticationField: () => void;
  environment: EnvironmentVariablesSchema;
  getSuggestions: (operation: string) => Array<string>;
  getTestLogs: () => Promise<any>;
  isReadOnly: boolean;
  isSaving: boolean;
  oAuthRedirectUri?: string;
  onDeleteConnectionLabelCode: () => void;
  onSaveAuthenticationSettings: (options: {}) => void;
  params: VersionParams;
  query: QueryHighlight;
  setUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  triggersDefinition: Record<string, TriggerSchema>;
};

type State = {
  activeStep: number;
  boolFieldKeys: string[];
  credentialFormFields: Record<string, any>;
  formValues: FormValues;
  highlightField: ApiHighlightField;
  isPkceEnabled: boolean;
  modifiedConnectionLabelCode: string;
  modifiedConnectionLabelText: string;
  shouldEditConnectionLabelCode: boolean;
  testTriggerKeyLegacy?: string;
};

class AuthenticationEditor extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { authentication, authenticationLegacy, environment, query } = props;

    const { connectionLabel, test: authTest, type: authType } = authentication;

    // build env values map
    const authFields = getAuthFieldsForAuthType(authType);
    const boolFieldKeys = [...commonFields, ...authFields]
      .filter(f => f.type === 'boolean-input')
      .map(f => f.key);
    const envKeys = authFields.filter(f => f.env).map(f => f.key);
    const envValues = pick(environment, envKeys);

    const isLegacyAuth = this.isLegacyAuth();
    const isLegacyOAuth2 = this.isLegacyOAuth2();
    const testTriggerKeyLegacy = authenticationLegacy?.testTrigger;

    const test = isLegacyAuth
      ? this.getTestForLegacy(testTriggerKeyLegacy)
      : authTest;

    const authConfig = authentication?.[`${authType}Config`];
    const isPkceEnabled =
      (authType === 'oauth2' && authConfig?.enablePkce) ?? false;

    this.state = {
      modifiedConnectionLabelText:
        typeof connectionLabel === 'object' ? '' : connectionLabel,
      modifiedConnectionLabelCode:
        typeof connectionLabel === 'object' ? connectionLabel.source : '',
      shouldEditConnectionLabelCode: typeof connectionLabel === 'object',

      formValues: {
        // merge env values, common field values, and config field values
        ...(isLegacyOAuth2
          ? DEFAULT_REQUEST_CONFIGS.oauth2Legacy
          : DEFAULT_REQUEST_CONFIGS[authType]),
        ...(isLegacyOAuth2
          ? {
              ...authenticationLegacy.oauth2Config,
              ...pick(authConfig, ['autoRefresh', 'scope']),
            }
          : authConfig),
        ...envValues,
        test,
      },
      boolFieldKeys,
      activeStep: this.getActiveStep(query, authType),
      highlightField: this.getFieldToHighlight(query),
      ...(isLegacyAuth
        ? { testTriggerKeyLegacy: authenticationLegacy.testTrigger }
        : {}),
      isPkceEnabled,
      credentialFormFields: credentialFields[authType],
    };
  }

  isLegacyAuth = () => {
    const { authenticationLegacy } = this.props;
    return !isEmpty(authenticationLegacy);
  };

  isLegacyOAuth2 = () => {
    const {
      authentication: { type: authType },
    } = this.props;
    const isOAuth2 = authType === 'oauth2';
    const isLegacyAuth = this.isLegacyAuth();
    return isOAuth2 && isLegacyAuth;
  };

  getActiveStep = (query: QueryHighlight, authType: string) => {
    return query?.highlight
      ? getAuthenticationQueryHighlight(query.highlight, authType, 1)
      : 1;
  };

  getFieldToHighlight(
    query: QueryHighlight = { highlight: '', type: '', message: '' }
  ) {
    const { highlight = '', type = '', message = '' } = query;

    return {
      field: getHighlightAuthenticationFieldKey(highlight),
      type,
      message,
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { authentication, query } = this.props;
    const { query: prevQuery } = prevProps;

    const { type: authType } = authentication;
    if (query && query.highlight !== prevQuery.highlight) {
      this.setState({
        highlightField: this.getFieldToHighlight(query),
        activeStep: this.getActiveStep(query, authType),
      });
    }
  }

  setUnsavedChanges = () => this.props.setUnsavedChanges(true);
  unsetUnsavedChanges = () => this.props.setUnsavedChanges(false);

  get authHelpText() {
    const authType = this.props.authentication.type;
    return AUTH_TYPE_HELP_TEXT[authType];
  }

  getAdditionalHelpTextByType(type: string) {
    return INPUT_FIELDS_ADDITIONAL_HELP_TEXT[type] ?? '';
  }

  setActiveStepAndMaybeSave = ({
    step,
    shouldSave,
  }: {
    step: number;
    shouldSave: boolean;
  }) => {
    if (shouldSave) {
      this.onSaveAuthenticationSettings();
    }
    this.setState({
      activeStep: step,
    });
  };

  onFormChange = (value: string | boolean | {}, key: string) => {
    const { boolFieldKeys, formValues } = this.state;
    const formValuesNext = {
      ...formValues,
      [key]: boolFieldKeys.includes(key) ? !formValues[key] : value,
    };

    if (!isEqual(formValuesNext, formValues)) {
      this.setState({ formValues: formValuesNext }, this.setUnsavedChanges);
    }
  };

  getTestForLegacy = (triggerKey: string) => {
    return {
      source:
        triggerKey === ''
          ? 'throw new Error("test trigger is not defined");'
          : `return z.legacyScripting.run(bundle, 'trigger', '${triggerKey}');`,
    };
  };

  onChangeTestTriggerLegacy = (triggerKey: string) => {
    const { formValues } = this.state;

    this.setState(
      {
        formValues: {
          ...formValues,
          test: this.getTestForLegacy(triggerKey),
        },
        testTriggerKeyLegacy: triggerKey,
      },
      this.setUnsavedChanges
    );
  };

  onChangeConnectionLabelText = (value: string) => {
    this.setState(
      { modifiedConnectionLabelText: value },
      this.setUnsavedChanges
    );
  };

  onChangeConnectionLabelCode = (value: string) => {
    this.setState(
      { modifiedConnectionLabelCode: value },
      this.setUnsavedChanges
    );
  };

  onDeleteConnectionLabelCode = () => {
    this.props.onDeleteConnectionLabelCode();
    this.setState(
      {
        modifiedConnectionLabelCode: '',
        shouldEditConnectionLabelCode: false,
      },
      this.setUnsavedChanges
    );
  };

  onUndoConnectionLabelCode = () => {
    const { connectionLabel } = this.props.authentication;

    if (typeof connectionLabel !== 'object') {
      this.setState(
        {
          modifiedConnectionLabelCode: '',
          shouldEditConnectionLabelCode: false,
        },
        this.setUnsavedChanges
      );
    } else {
      this.setState(
        {
          modifiedConnectionLabelCode: connectionLabel.source,
        },
        this.setUnsavedChanges
      );
    }
  };

  onToggleEditConnectionLabelCode = () => {
    this.setState(
      {
        shouldEditConnectionLabelCode: !this.state
          .shouldEditConnectionLabelCode,
      },
      this.setUnsavedChanges
    );
  };

  onOAuthSetupSelect = (selectedType: string) => {
    const isPkceSelected = selectedType === 'pkce';
    const { credentialFormFields, formValues } = this.state;

    // Set CLIENT_SECRET required to false if PKCE is selected
    const newCredentialFields = credentialFormFields.map(field => {
      if (field.key === constants.OAUTH_CLIENT_SECRET) {
        return {
          ...field,
          required: !isPkceSelected,
        };
      }
      return field;
    });

    this.setState(
      {
        credentialFormFields: newCredentialFields,
        isPkceEnabled: isPkceSelected,
        formValues: {
          ...formValues,
          // Add 'code_verifier' if PKCE is enabled
          enablePkce: isPkceSelected,
          getAccessToken: {
            ...formValues?.getAccessToken,
            body: {
              ...formValues?.getAccessToken?.body,
              ...(isPkceSelected
                ? {
                    code_verifier: '{{bundle.inputData.code_verifier}}',
                  }
                : { code_verifier: null }),
            },
          },
        },
      },
      this.setUnsavedChanges
    );
  };

  onSaveAuthenticationSettings = () => {
    const {
      shouldEditConnectionLabelCode,
      modifiedConnectionLabelText,
      modifiedConnectionLabelCode,
      formValues,
      testTriggerKeyLegacy,
    } = this.state;
    const { authentication } = this.props;
    const { type: authType } = authentication;
    const connectionLabel = shouldEditConnectionLabelCode
      ? { source: modifiedConnectionLabelCode }
      : modifiedConnectionLabelText;

    const authFields = getAuthFieldsForAuthType(authType);
    const envKeys = authFields.filter(f => f.env).map(f => f.key);

    const authSettings = {
      connectionLabel,
      envVariables: pick(formValues, envKeys),
      ...pick(formValues, commonFieldKeys),
      testTriggerKeyLegacy,
    };

    // pull out top-level+env values from the form
    const localFormValues = omit(formValues, [...envKeys, ...commonFieldKeys]);

    const isLegacyOAuth2 = this.isLegacyOAuth2();
    if (!isLegacyOAuth2) {
      const removeNonRedirectParamsKeys = authFields
        .filter(authField => authField.removeNonRedirectParams)
        .map(authField => authField.key);

      const redirectRequestSchemaProps = ['method', 'url', 'params'];
      const functionSourceSchemaProps = ['args', 'source'];

      removeNonRedirectParamsKeys.forEach(k => {
        localFormValues[k] = pick(
          localFormValues[k],
          localFormValues[k]?.source
            ? functionSourceSchemaProps // code mode
            : redirectRequestSchemaProps // form mode
        );
      });
    }

    if (authType && localFormValues) {
      authSettings[`${authType}Config`] = localFormValues;
    }

    this.props.onSaveAuthenticationSettings(authSettings);
    this.unsetUnsavedChanges();
  };

  render() {
    const {
      activeStep,
      formValues,
      highlightField,
      modifiedConnectionLabelText,
      modifiedConnectionLabelCode,
      shouldEditConnectionLabelCode,
      testTriggerKeyLegacy,
      isPkceEnabled,
      credentialFormFields,
    } = this.state;

    const {
      authentication,
      deleteAuthenticationField,
      getSuggestions,
      getTestLogs,
      fields,
      isReadOnly,
      isSaving,
      oAuthRedirectUri,
      params,
      query,
      triggersDefinition,
    } = this.props;

    const { type: authType } = authentication;
    const authScheme =
      find(AUTH_SCHEMES, ['value', authType]) || AUTH_SCHEMES[0];
    const isBasicAuth = authType === 'basic';
    const isOAuth2 = authType === 'oauth2';
    const isLegacyAuth = this.isLegacyAuth();
    const isLegacyOAuth2 = this.isLegacyOAuth2();

    const appVersionUrl = getAppVersionUrl(params.id, params.version);

    const tokenExchangeFormFields = tokenExchangeFields[authType];
    const configFormFields = isLegacyAuth
      ? configFieldsLegacy[authType] ?? []
      : [...(configFields[authType] ?? []), ...commonFields];

    // TODO: validate form
    const isFormValid = true;

    const requestsForTesting = [
      {
        label: 'Test',
        operationMethod: 'test',
        request: authentication.test,
      },
    ];

    const currentStep = `Step ${activeStep}`;
    const stepsHelp =
      isBasicAuth && isLegacyAuth
        ? STEPS_HELP_BY_TYPE[authType + 'Legacy']
        : STEPS_HELP_BY_TYPE[authType];

    const deepLinkInfo = getDeepLinkMetaInfoFromQuery(query);
    const setupStep = SETUP_STEPS[authType];

    // OAuth/Pkce setup options if applicable
    const oauth2SetupCredentials = [
      {
        type: 'oauth2',
        credentials: { ...stepsHelp.credentials, heading: 'OAuth v2' },
      },
      {
        type: 'pkce',
        credentials: {
          ...STEPS_HELP_BY_TYPE.pkce.credentials,
          heading: 'OAuth v2 with PKCE Extension',
        },
      },
    ];
    const Options = oauth2SetupCredentials.map(({ type, credentials }) => (
      <OptionBlock
        key={type}
        onSelect={() => this.onOAuthSetupSelect(type)}
        option={credentials}
        selected={(isPkceEnabled ? 'pkce' : 'oauth2') === type}
        value={type}
      />
    ));

    const triggers = Object.values(triggersDefinition || {});

    return (
      <EditorLayout title={(authScheme && authScheme.label) || ''}>
        <EditorBodyLayout
          css={Styles.root}
          isDisabled={isReadOnly || !isFormValid}
          isSaving={isSaving}
        >
          <HelpBox
            description={this.authHelpText.description}
            heading={`What is ${this.authHelpText.heading}?`}
          />
          <SetupStep
            helpText={{
              heading: stepsHelp.fields?.heading,
              description: (
                <Fragment>
                  <Text tag="p">{stepsHelp.fields?.description}</Text>
                  <Text fontWeight={600} margin="10px 0" tag="p">
                    {this.getAdditionalHelpTextByType(authentication.type)}
                  </Text>
                  <Text margin="10px 0" tag="p">
                    Note: Fields that are added here will automatically get
                    added to the{' '}
                    <a href={`${appVersionUrl}/advanced/request-template`}>
                      Request Template
                    </a>
                    .
                  </Text>
                </Fragment>
              ),
            }}
            isInitiallyOpen={setupStep?.fields === currentStep}
            stepLabel={setupStep?.fields ?? ''}
            title="Configure your Fields"
          >
            <AuthenticationFields
              deleteAuthenticationField={deleteAuthenticationField}
              fields={fields}
              isReadOnly={isReadOnly}
              params={params}
            />
            <SaveButtonGroup
              cancelRoute={`${appVersionUrl}/authentication/fields/${NEW_SERVICE_FIELD}`}
              cancelText="Add Fields"
              isCancelDisabled={isReadOnly}
              isDisabled={isReadOnly}
              isSaving={isSaving}
              onSave={() =>
                this.setActiveStepAndMaybeSave({
                  step: removeNonNumericAndCastToNumber(setupStep?.fields) + 1,
                  shouldSave: false,
                })
              }
              saveText="Continue"
            />
          </SetupStep>
          {!isBasicAuth && oAuthRedirectUri && (
            <SetupStep
              helpText={{
                heading: stepsHelp.redirectUrl?.heading,
                description: (
                  <Fragment>
                    <Text tag="p">{stepsHelp.redirectUrl?.description}</Text>
                    {isLegacyAuth && (
                      <Text fontWeight={600} margin="10px 0" tag="p">
                        {this.getAdditionalHelpTextByType('legacy')}
                      </Text>
                    )}
                  </Fragment>
                ),
              }}
              isInitiallyOpen={setupStep?.redirectUri === currentStep}
              stepLabel={setupStep?.redirectUri ?? ''}
              title="Copy your OAuth Redirect URL"
            >
              <Text margin="0px 0px 10px 0px" tag="p">
                Copy Zapier's OAuth Redirect URL below, and add it to the
                allowed list in your app’s admin console if needed.
              </Text>
              <CopyTextInput
                aria-label="Zapier's OAuth Redirect URL"
                value={oAuthRedirectUri || ''}
              />
              <SaveButtonGroup
                isDisabled={isReadOnly}
                isSaving={isSaving}
                onSave={() =>
                  this.setActiveStepAndMaybeSave({
                    step:
                      removeNonNumericAndCastToNumber(setupStep?.redirectUri) +
                      1,
                    shouldSave: false,
                  })
                }
                saveText="Continue"
              />
            </SetupStep>
          )}
          {credentialFormFields && (
            <SetupStep
              helpText={
                isOAuth2
                  ? {
                      description: <div>{Options}</div>,
                    }
                  : stepsHelp.credentials
              }
              isInitiallyOpen={setupStep?.credentials === currentStep}
              stepLabel={setupStep?.credentials ?? ''}
              title="Enter your Application Credentials"
            >
              <DeveloperFormFields
                fields={credentialFormFields}
                meta={{
                  getSuggestions,
                  info: deepLinkInfo,
                }}
                onFormChange={this.onFormChange}
                values={formValues}
              />
              <SaveButtonGroup
                isDisabled={isReadOnly || !isFormValid}
                isSaving={isSaving}
                onSave={() => {
                  this.setActiveStepAndMaybeSave({
                    step:
                      removeNonNumericAndCastToNumber(setupStep?.credentials) +
                      1,
                    shouldSave: true,
                  });
                }}
                saveText="Save &amp; Continue"
              />
            </SetupStep>
          )}
          {tokenExchangeFormFields && (
            <SetupStep
              isInitiallyOpen={setupStep?.tokenExchange === currentStep}
              stepLabel={setupStep?.tokenExchange ?? ''}
              title="Configure a Token Exchange Request"
            >
              <DeveloperFormFields
                fields={tokenExchangeFormFields}
                meta={{
                  getSuggestions,
                  info: deepLinkInfo,
                }}
                onFormChange={this.onFormChange}
                values={formValues}
              />
              <SaveButtonGroup
                isDisabled={isReadOnly || !isFormValid}
                isSaving={isSaving}
                onSave={() => {
                  this.setActiveStepAndMaybeSave({
                    step:
                      removeNonNumericAndCastToNumber(
                        setupStep?.tokenExchange
                      ) + 1,
                    shouldSave: true,
                  });
                }}
                saveText="Save &amp; Continue"
              />
            </SetupStep>
          )}
          <SetupStep
            helpText={isLegacyOAuth2 ? '' : stepsHelp.endpoints}
            isInitiallyOpen={setupStep.configuration === currentStep}
            stepLabel={setupStep.configuration ?? ''}
            title={
              isLegacyOAuth2
                ? 'Legacy OAuth2 Endpoint Configuration'
                : isOAuth2
                ? `Add ${authScheme.label} Endpoint Configuration`
                : 'Configure a Test Request & Connection Label'
            }
          >
            <DeveloperFormFields
              fields={configFormFields}
              meta={{
                getSuggestions,
                info: deepLinkInfo,
              }}
              onFormChange={this.onFormChange}
              values={formValues}
            />
            {isLegacyAuth && !!triggers.length && (
              <Margin margin="30px 0 0">
                <TestTriggerDropdown
                  onChange={this.onChangeTestTriggerLegacy}
                  triggerKey={testTriggerKeyLegacy}
                  triggers={triggers}
                />
              </Margin>
            )}
            <Margin margin="30px 0 0 0">
              <NonFormaticFieldWrapper
                helpText={stepsHelp.connectionLabel}
                title="Connection Label"
              >
                <TextOrCodeInput
                  codeData={modifiedConnectionLabelCode}
                  isDisabled={isReadOnly}
                  meta={
                    highlightField.field ===
                      highlightAuthFieldKey.connectionLabel && {
                      info: highlightField,
                    }
                  }
                  onCancelCode={this.onUndoConnectionLabelCode}
                  onChangeCode={this.onChangeConnectionLabelCode}
                  onChangeText={this.onChangeConnectionLabelText}
                  onClearAllCode={this.onDeleteConnectionLabelCode}
                  onToggleEditCode={this.onToggleEditConnectionLabelCode}
                  shouldEditCode={shouldEditConnectionLabelCode}
                  textData={modifiedConnectionLabelText}
                  textLabel="Connection Label"
                />
              </NonFormaticFieldWrapper>
            </Margin>
            <SaveButtonGroup
              isDisabled={isReadOnly || !isFormValid}
              isSaving={isSaving}
              onSave={() => {
                this.setActiveStepAndMaybeSave({
                  step:
                    removeNonNumericAndCastToNumber(setupStep?.configuration) +
                    1,
                  shouldSave: true,
                });
              }}
              saveText="Save &amp; Continue"
            />
          </SetupStep>
          <SetupStep
            isInitiallyOpen={setupStep?.testing === currentStep}
            onExpand={getTestLogs}
            stepLabel={setupStep?.testing ?? ''}
            title="Test your Authentication"
          >
            <div>
              <NonFormaticFieldWrapper
                isTitleOnly={true}
                title={
                  (requestsForTesting.length > 1 && 'Select a Request') || ''
                }
              >
                <RequestTester
                  appId={Number(params.id)}
                  canConnectAuth={authMeetsRequirements(authentication)}
                  meta={
                    highlightField.field === highlightAuthFieldKey.test && {
                      info: highlightField,
                    }
                  }
                  needsAuthMessage="Add an Input Field in Step 1 to test your authentication."
                  objectKey="test"
                  requests={requestsForTesting}
                  testType="authentication"
                  versionNumber={params.version}
                />
              </NonFormaticFieldWrapper>
            </div>
            <SaveButtonGroup
              isDisabled={isReadOnly || !isFormValid}
              isSaving={isSaving}
              onSave={() => {
                this.setActiveStepAndMaybeSave({
                  step: removeNonNumericAndCastToNumber(setupStep?.testing) + 1,
                  shouldSave: true,
                });
              }}
              saveText="Save &amp; Finish"
            />
          </SetupStep>
        </EditorBodyLayout>
      </EditorLayout>
    );
  }
}

export default AuthenticationEditor;
